/**
 * Rosie -- NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */

/**
 * LGH iPad room page (PB-2). A shared-kiosk surface, NOT behind the admin
 * login. Access is a long-lived device token: it arrives once in the URL
 * (?token=...), is stored on the device, and is sent as X-Device-Token on
 * every /api/chat call. The token is room-scoped server-side (chatAuth +
 * enforceRoomScope), so this page can only ever see the LGH room -- no other
 * facility's data, and driver/vehicle identity is already stripped from LGH
 * room content upstream. Per-message attribution is a tap-your-name chip.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

const BLUE = '#185FA5';

function getStoredToken() {
  return localStorage.getItem('lgh_device_token') || '';
}

// Device-token fetch. Never uses the admin Bearer client.
async function apiFetch(path, { method = 'GET', body } = {}) {
  const token = getStoredToken();
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      'X-Device-Token': token,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  return res;
}

function Message({ m, myName }) {
  if (m.message_type === 'booking_card') {
    return (
      <div style={{ alignSelf: 'stretch', background: '#e7f6f0', border: '1px solid #b7e3d2', borderRadius: 12, padding: 16, margin: '6px 0' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#085041', marginBottom: 4 }}>✓ Booking confirmed</div>
        <div style={{ fontSize: 16, color: '#085041', whiteSpace: 'pre-wrap' }}>{m.body}</div>
      </div>
    );
  }
  if (m.message_type === 'system_event' || m.author_type === 'system') {
    return (
      <div style={{ alignSelf: 'center', fontSize: 13, color: '#888', fontStyle: 'italic', textAlign: 'center', margin: '6px 0', maxWidth: '90%' }}>
        {m.body}
      </div>
    );
  }
  const isRosie = m.author_type === 'rosie';
  const mine = !isRosie && m.author_name === myName;
  return (
    <div style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '80%', margin: '6px 0' }}>
      <div style={{ fontSize: 12, color: '#999', marginBottom: 2, padding: '0 4px' }}>
        {isRosie ? 'Rosie' : (m.author_name || 'LGH staff')} · {new Date(m.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })}
      </div>
      <div style={{
        background: isRosie ? '#f0f2f5' : (mine ? BLUE : '#fff'),
        color: isRosie ? '#222' : (mine ? '#fff' : '#222'),
        border: mine ? 'none' : '1px solid #e3e3e0',
        borderRadius: 16, padding: '12px 16px', fontSize: 17, whiteSpace: 'pre-wrap', lineHeight: 1.4,
      }}>
        {m.body}
      </div>
    </div>
  );
}

export default function LghRoom() {
  const [status, setStatus] = useState('loading'); // loading | ready | no-token | bad-token | error
  const [messages, setMessages] = useState([]);
  const [roster, setRoster] = useState([]);
  const [myName, setMyName] = useState(localStorage.getItem('lgh_staff_name') || '');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const lastIdRef = useRef(null);

  // Capture a token handed off in the URL, persist it, then scrub the URL so
  // the secret isn't left in the address bar / history on a shared device.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      localStorage.setItem('lgh_device_token', urlToken);
      window.history.replaceState({}, '', '/lgh-room');
    }
  }, []);

  const loadMessages = useCallback(async () => {
    const res = await apiFetch('/chat/rooms/lgh/messages');
    if (!res.ok) return res.status;
    const data = await res.json();
    setMessages(data.messages || []);
    if (data.messages && data.messages.length) {
      lastIdRef.current = data.messages[data.messages.length - 1].id;
    }
    return 200;
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!getStoredToken()) { setStatus('no-token'); return; }
      const who = await apiFetch('/chat/whoami');
      if (!alive) return;
      if (who.status === 401) { setStatus('bad-token'); return; }
      if (!who.ok) { setStatus('error'); return; }
      const info = await who.json();
      if (info.kind !== 'device' || info.roomKey !== 'lgh') { setStatus('bad-token'); return; }
      try {
        const r = await apiFetch('/chat/lgh-roster');
        if (r.ok) setRoster((await r.json()).names || []);
      } catch (e) { /* roster is optional */ }
      const code = await loadMessages();
      if (!alive) return;
      setStatus(code === 200 ? 'ready' : 'error');
    })();
    return () => { alive = false; };
  }, [loadMessages]);

  // Poll for new messages every 5s while ready.
  useEffect(() => {
    if (status !== 'ready') return undefined;
    const id = setInterval(loadMessages, 5000);
    return () => clearInterval(id);
  }, [status, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const pickName = (n) => { setMyName(n); localStorage.setItem('lgh_staff_name', n); };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    if (!myName) return; // must tap a name first
    setSending(true);
    setInput('');
    try {
      const res = await apiFetch('/chat/rooms/lgh/messages', {
        method: 'POST',
        body: { body: text, authorName: myName },
      });
      if (res.status === 401) { setStatus('bad-token'); return; }
      await loadMessages();
    } catch (e) {
      // surface a soft error; the message list reload will reconcile
    } finally {
      setSending(false);
    }
  };

  if (status === 'loading') {
    return <Centered>Loading…</Centered>;
  }
  if (status === 'no-token' || status === 'bad-token') {
    return (
      <Centered>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>This iPad isn’t set up yet</div>
        <div style={{ color: '#666', maxWidth: 420, textAlign: 'center' }}>
          Ask Commonwealth dispatch for a fresh room link. {status === 'bad-token' && 'The previous link is no longer valid.'}
        </div>
      </Centered>
    );
  }
  if (status === 'error') {
    return <Centered><div>Couldn’t load the room. Please try again in a moment.</div></Centered>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f5f5f3', fontFamily: '-apple-system, sans-serif' }}>
      <div style={{ background: BLUE, color: '#fff', padding: '14px 18px', fontSize: 18, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Commonwealth Transport — LGH Booking Room</span>
        <span style={{ fontSize: 13, opacity: 0.85 }}>{myName ? `You: ${myName}` : 'Tap your name below'}</span>
      </div>
      {/* AI disclosure at the top of every session (Anthropic usage policy effective 2026-11-12). */}
      <div style={{ background: '#EDF4FB', color: '#185FA5', padding: '6px 18px', fontSize: 13, borderBottom: '0.5px solid #B5D4F4' }}>
        Rosie is Commonwealth Transport's automated dispatch assistant (AI). A Commonwealth dispatcher reviews every booking before a driver is sent.
      </div>

      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column' }}>
        {messages.length === 0 && (
          <div style={{ color: '#999', textAlign: 'center', marginTop: 40 }}>
            No messages yet. Type a transport request below — Rosie will confirm it.
          </div>
        )}
        {messages.map((m) => <Message key={m.id} m={m} myName={myName} />)}
        {sending && <div style={{ alignSelf: 'flex-start', fontSize: 13, color: '#999', fontStyle: 'italic', padding: '4px 8px' }}>Rosie is thinking…</div>}
      </div>

      {/* Tap-your-name chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 12px', background: '#ececea', borderTop: '1px solid #ddd' }}>
        {roster.length === 0 && <span style={{ fontSize: 13, color: '#888' }}>No names configured — messages post as “LGH staff”.</span>}
        {roster.map((n) => (
          <button key={n} onClick={() => pickName(n)}
            style={{
              padding: '8px 16px', borderRadius: 18, fontSize: 15, cursor: 'pointer',
              border: myName === n ? `2px solid ${BLUE}` : '1px solid #ccc',
              background: myName === n ? BLUE : '#fff', color: myName === n ? '#fff' : '#333', fontWeight: myName === n ? 600 : 400,
            }}>
            {n}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, padding: 12, background: '#fff', borderTop: '1px solid #e0e0db' }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={myName ? 'Type a transport request…' : 'Tap your name above first'}
          disabled={!myName}
          style={{ flex: 1, fontSize: 17, padding: '14px 16px', border: '1px solid #ddd', borderRadius: 24, outline: 'none', background: myName ? '#fff' : '#f3f3f1' }}
        />
        <button onClick={send} disabled={sending || !input.trim() || !myName}
          style={{ background: BLUE, color: '#fff', border: 'none', borderRadius: 24, padding: '14px 28px', fontSize: 16, fontWeight: 600, cursor: 'pointer', opacity: (sending || !input.trim() || !myName) ? 0.5 : 1 }}>
          Send
        </button>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f5f5f3', fontFamily: '-apple-system, sans-serif', padding: 24 }}>
      {children}
    </div>
  );
}
